import React, { Fragment } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import colors from '../../configuration/colors'
import truncateMiddle from 'truncate-middle'
import FileUploadProgressBar from '../mini/FileUploadProgressBar'
import { gbInBytes, validateFileSize, getMD5Checksum } from '../../utils/file'

const FlexContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
`

const Label = styled.label`
  display: inline-block;
  border: 1px solid ${colors.aluminum};
  background: ${colors.white};
  color: ${colors.black};
  border-radius: 100px;
  padding: 12px 22px;
  margin-bottom: 10px;
   &:hover {
    color: ${colors.white};
    background: ${colors.charcoal2};
    border: 1px solid ${colors.charcoal2};
  }
`

class S3Uploader extends React.Component {
  constructor() {
    super()
    this.defaultState = {
      failure: null,
      isUploading: false,
      progress: 0,
      fileSizeExceeded: false,
      fileName: '',
      xhr: null
    }
    this.state = {
      ...this.defaultState
    }

    this.onUpload = this.onUpload.bind(this)
    this.updateProgress = this.updateProgress.bind(this)
    this.abortRequest = this.abortRequest.bind(this)
  }

  render() {
    const { resourceType } = this.props
    return (
      <Fragment>
        {!this.state.isUploading ? (
          <div>
            {this.props.isActivityCourse
              ? <FlexContainer>
                <Label htmlFor="file-input-s3">
                  Choose File
                </Label>
                <p style={{marginLeft: '10px'}}>No file chosen</p>
                <input
                  id="file-input-s3"
                  type="file"
                  onChange={this.onUpload}
                  style={{display: 'none'}}
                />
              </FlexContainer>
              : <input type="file" onChange={this.onUpload}/>
            }
            <label className="hint-text">
              {resourceType === 'video' &&
                'Videos must be under 1 GB and a .3gp, .mp4, .mkv, or .webm file type.'}
              {resourceType !== 'video' && 'File size limit is 1 GB. '}
              {this.state.fileSizeExceeded && (
                <span className="error-text" style={{ display: 'block' }}>
                  File size limit is 1 GB.
                </span>
              )}
              {this.state.failure && (
                <div>
                  <label className="error-text">
                    {this.state.failure.type === 'expired'
                      ? 'File upload session expired. Please refresh your browser and try again.'
                      : 'File upload failed. Please try again.'}
                  </label>
                </div>
              )}
            </label>
          </div>
        ) : (
          <Fragment>
            <div style={{ marginBottom: '8px' }}>
              {truncateMiddle(this.state.fileName, 50, 50, '...')}
            </div>
            <FileUploadProgressBar progress={this.state.progress} />
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-end',
                justifyContent: 'space-between'
              }}
            >
              <label
                style={{
                  marginTop: '7px'
                }}
                className="hint-text"
              >
                Please, keep this page open until upload is complete to save
                this resource.
              </label>
              <span
                onClick={() => this.abortRequest()}
                style={{
                  color: '#0070E0',
                  fontSize: '13px',
                  fontWeight: 500,
                  cursor: 'pointer'
                }}
              >
                Cancel
              </span>
            </div>
          </Fragment>
        )}
      </Fragment>
    )
  }

  updateProgress(event) {
    if (event.lengthComputable) {
      const progress = parseInt((event.loaded / event.total) * 100)
      this.setState(() => ({
        progress
      }))
    }
  }

  async onUpload(e) {
    const { presignedUrl, resourceId, uniqueFilename } = this.props
    const file = e.target.files[0]
    const objSon = presignedUrl
    const url = objSon.url
    const fileName = `${uniqueFilename}.${/[^.]+$/.exec(file.name)[0]}`
    const startTime = performance.now()
    const self = this

    // validate file size. return and setState if too big
    if (!validateFileSize(e.target.files[0], gbInBytes(1))) {
      this.setState(() => ({
        fileSizeExceeded: true
      }))
      return
    }

    objSon.url_fields.key = `uploads/resource/resource_file/${resourceId}/${fileName}`
    let xhr = new XMLHttpRequest()
    let fd = new FormData()

    xhr.upload.addEventListener('progress', this.updateProgress, false)

    const checksumPromise = getMD5Checksum(file)

    const uploadPromise = new Promise((resolve) => 
      this.setState(() => ({
          isUploading: true,
          failure: null,
          fileName: file.name,
          fileSizeExceeded: false,
          xhr
      }), resolve))
      // send analytics data/callback to parent
      .then(() => this.props.uploadStartHandler({
        name: file.name,
        size: file.size
      }))
      .then(() => new Promise((resolve, reject) => {
        xhr.open('POST', url, true)
        
        Object.entries(objSon.url_fields).forEach(([key, value]) => {
          fd.append(key, value)
        })
        fd.append('Content-Type', file.type)
        fd.append('file', file)

        xhr.onload = () => {
          if (xhr.status === 201) {
            const endTime = performance.now()
            const elapsedTime = Math.floor((endTime - startTime) / 1000)
            resolve(elapsedTime)
          } else if (xhr.status === 403) {
            reject('expired')
          } else {
            reject('generic')
          }
        }
  
        xhr.onerror = () => reject('generic')

        xhr.send(fd)
        xhr.file = file
        xhr.fileName = fileName
      }))

    Promise.all([checksumPromise, uploadPromise])
      .then((values) => {
        const [checksum, elapsedTime] = values
        this.props.uploadCompleteHandler({
          file: {
            name: xhr.file.name,
            size: file.size
          },
          elapsedTime
        })
        return this.props.patchFileUploadInfo({
          name: xhr.fileName,
          size: xhr.file.size,
          type: xhr.file.type,
          original_file_name: xhr.file.name,
          file_checksum: checksum
        })
      })
      .then(() => new Promise((resolve) =>
        this.setState(() => ({
          ...this.defaultState
        }), resolve)
      ))
      .catch(err => new Promise((resolve) =>
        this.setState(() => ({
          ...this.defaultState,
          failure: { type: err === 'expired' ? err : 'generic' }
        }), resolve)
      ))
      .then(() => this.props.uploadEndHandler())
  }

  async abortRequest(source) {
    const { file } = this.state.xhr
    if (source) {
      this.props.uploadAbortHandler({
        file: {
          name: file.name,
          size: file.size
        },
        source
      })
      await this.state.xhr.abort()
      this.setState(() => ({
        ...this.defaultState
      }))
    } else {
      const confirmation = window.confirm(
        'Are you sure you want to cancel this upload?'
      )
      if (confirmation) {
        this.props.uploadAbortHandler({
          file: {
            name: file.name,
            size: file.size
          },
          source: 'bar cancel'
        })
        await this.state.xhr.abort()
        this.setState(() => ({
          ...this.defaultState
        }))
      }
    }
  }
}

S3Uploader.propTypes = {
  presignedUrl: PropTypes.object.isRequired,
  patchFileUploadInfo: PropTypes.func.isRequired,
  resourceType: PropTypes.string.isRequired,
  resourceId: PropTypes.number.isRequired,
  uploadStartHandler: PropTypes.func.isRequired,
  uploadEndHandler: PropTypes.func.isRequired,
  uploadAbortHandler: PropTypes.func.isRequired,
  uploadCompleteHandler: PropTypes.func.isRequired,
  uniqueFilename: PropTypes.string.isRequired,
  isActivityCourse: PropTypes.bool.isRequired
}

export default S3Uploader



// WEBPACK FOOTER //
// ./src/components/activityedit/S3Uploader.js